import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../ReduxToolkit/Hooks";
import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import TitleAndClientSection from "./TitleAndClientSection";
import {
  userInitialValue,
  userValidation,
  authorInitialValue,
  authorValidation,
} from "../../../../Data/Application/ProjectList/ProjectList";
import ProjectSection from "./ProjectSection";
import UploadProjectFile from "./UploadProjectFile";
import ButtonSection from "./ButtonSection";
import {
  UserInitialValue,
  AuthorInitialValue,
} from "../../../../Types/Application/ProjectList/ProjectList";
import { setCreatedData } from "../../../../ReduxToolkit/Reducer/ProjectSlice";
import apiRequestHelper from "../../../../utils/apiRequestHelper";
import { toast } from "react-toastify";
import { useLocation, useParams } from "react-router-dom";
import * as Yup from "yup";
import AuthorSection from "./AuthorSection";
import RichEditor from "./RichEditior";
import LexicalEditor from "./LexicalEditor";
import SlateEditor from "./SlateEditor";
import FAQSection from "./FAQSection";
interface Heading {
  title: string;
  paragraphs: any[]; // or a more specific structure if you know the shape
}
interface FAQ {
  question: string;
  answer: string;
}

interface Blog {
  title: string;
  image: File | null;
  slug: string;
  authorId: number;
  scheduledDate?: string;
  content_json: any; // tiptap JSON
  content_html: string;
  faqs: FAQ[];
}
function tiptapJsonHasText(doc: any): boolean {
  if (!doc || doc.type !== "doc" || !Array.isArray(doc.content)) return false;

  const nodeToText = (node: any): string => {
    if (!node) return "";
    if (node.type === "text") return node.text || "";
    if (Array.isArray(node.content))
      return node.content.map(nodeToText).join("");
    return "";
  };

  const text = doc.content.map(nodeToText).join("").trim();
  return text.length > 0; // adjust if you want a min length
}

// Heading schema
const HeadingSchema = Yup.object().shape({
  title: Yup.string()
    .required("Heading title is required")
    .min(3, "Heading title must be at least 3 characters"),

  paragraphs: Yup.array()
    .of(Yup.string().required("Paragraph cannot be empty"))
    .min(1, "At least one paragraph is required"),
});
const BlogSchema = Yup.object().shape({
  title: Yup.string()
    .required("Blog title is required")
    .min(5, "Title must be at least 5 characters"),

  image: Yup.mixed()
    .required("Image is required")
    .test("fileType", "Unsupported file format", (value) => {
      if (!value) return false;

      const file = value as File;
      return ["image/jpeg", "image/png", "image/jpg", "image/webp"].includes(
        file.type
      );
    })
    .test("fileSize", "File size must not exceed 10MB", (value) => {
      if (!value) return false;
      const file = value as File;
      return file.size <= 10 * 1024 * 1024; // 10 MB
    }),

  slug: Yup.string()
    .matches(
      /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
      "Slug must be URL-friendly (lowercase with dashes)"
    )
    .required("Slug is required"),

  authorId: Yup.number()
    .required("Author ID is required")
    .integer("Author ID must be a number"),

  scheduledDate: Yup.string()
    .nullable()
    .optional()
    .test(
      "is-valid-iso",
      "Scheduled date must be in ISO format (yyyy-mm-ddTHH:mm)",
      (value) => {
        if (!value) return true;
        return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(value); // basic ISO datetime check
      }
    ),

  // 👇 make editor required + has text
  content_json: Yup.mixed()
    .required("Content is required")
    .test("has-text", "Please add some content.", (val) =>
      tiptapJsonHasText(val)
    ),

  // keep html but we'll keep it in sync from the editor
  content_html: Yup.string().optional(),

  // FAQs validation
  faqs: Yup.array()
    .of(
      Yup.object().shape({
        question: Yup.string()
          .required("Question is required")
          .min(5, "Question must be at least 5 characters"),
        answer: Yup.string()
          .required("Answer is required")
          .min(10, "Answer must be at least 10 characters"),
      })
    )
    .optional(),
});
const CreateNewProjectForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPathname = location.pathname;
  const { id } = useParams(); // <-- Get blog ID if editing
  const [initialValues, setInitialValues] = useState<Blog>({
    title: "",
    image: null,
    slug: "",
    authorId: 0,
    scheduledDate: "",
    content_json: null,
    content_html: "",
    faqs: [],
  });

  const projectSubmit = async (values: Blog) => {
    const formData = new FormData();
    formData.append("title", values.title);
    formData.append("slug", values.slug);
    formData.append("authorId", values.authorId.toString());

    if (values.image) {
      formData.append("image", values.image); // ✅ required for upload
    }
    if (values.scheduledDate) {
      formData.append("scheduledDate", values.scheduledDate); // ✅ required for upload
    }
    console.log("scheduledDate", values.scheduledDate);
    // ✅ send editor outputs (server will derive headings from this JSON)
    formData.append("content_json", JSON.stringify(values.content_json));
    formData.append("content_html", values.content_html ?? "");

    // ✅ send FAQs if present
    if (values.faqs && values.faqs.length > 0) {
      formData.append("faqs", JSON.stringify(values.faqs));
    }
    // console.log("content_json", values.content_json);
    // console.log("content_html", values.content_html);
    // return;
    try {
      const response = await apiRequestHelper.post("/blogs", formData, true);

      const data = response.data;
      toast.success(`Blog created successfully!`);
      navigate("/blogs/cards");
    } catch (error: unknown) {
      console.error("Error submitting project:", error);
      // Optionally, show an error message to the user here
      const err = error as any;
      toast.error(
        err.response?.data?.messages?.[0]?.message || "Blog creation failed!."
      );
    }
  };
  return (
    <Formik<Blog>
      initialValues={initialValues}
      validationSchema={BlogSchema}
      onSubmit={projectSubmit}
    >
      {() => (
        <Form className="theme-form">
          <TitleAndClientSection />
          {/* <ProjectSection /> */}
          <AuthorSection />
          {/* <DateSection />
          <DescriptionSection /> */}
          <RichEditor />
          {/* <LexicalEditor /> */}
          {/* <SlateEditor /> */}
          <FAQSection />
          <UploadProjectFile />
          <ButtonSection />
        </Form>
      )}
    </Formik>
  );
};

export default CreateNewProjectForm;
